import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * FlowButton — pill button whose hover state floods in as a circular wash
 * from the corner (a `before` pseudo-element scaling into place). Ported from
 * Creator Studio v2 (`src/components/ui/flow-hover-button.tsx`) and
 * re-themed: the hardcoded navy palette is replaced with `--primary` /
 * `--accent` / `--border` tokens so the wash re-tints per studio.
 *
 * Sits between `<Button>` (everyday actions) and `<ShimmerButton>` (hero
 * moments): same sizes as Button, one extra dose of delight on hover.
 *
 * Onyx press: this is a PILL (`rounded-full`) signature-animated control, so it
 * stays round — no hex notch, no travel block, no keyline ring (those are for
 * rectangular keys and would clash with the round flood). It joins the lib's
 * shared press language with just the springy bounce + a 1px press-DOWN on
 * `:active` (the round/flat tier), replacing the old `active:scale-[0.98]`.
 *
 * - `icon` renders before the label, sized by the same `[&_svg]` rule Button uses
 * - the flood + press transitions collapse under `prefers-reduced-motion`
 *   (colours still change, nothing moves)
 *
 * @example
 *   <FlowButton icon={<ArrowRight />}>Continue</FlowButton>
 *   <FlowButton variant="ghost" size="sm">Skip for now</FlowButton>
 */
export const flowButtonVariants = cva(
  [
    "group relative z-0 inline-flex cursor-pointer items-center justify-center overflow-hidden rounded-full font-medium",
    "border",
    // One `transition` shorthand driving both motions (so the utilities don't
    // clobber each other): the colour / border flood keeps its slow smooth-out
    // timing, while transform rides the lib's springy overshoot bounce.
    "[transition:color_320ms_cubic-bezier(0.16,1,0.3,1),background-color_320ms_cubic-bezier(0.16,1,0.3,1),border-color_320ms_cubic-bezier(0.16,1,0.3,1),transform_150ms_cubic-bezier(0.34,1.45,0.6,1)]",
    "before:absolute before:inset-0 before:-z-10 before:rounded-[100%] before:content-['']",
    "before:translate-x-[150%] before:translate-y-[150%] before:scale-[2.5]",
    "before:transition-transform before:duration-700 before:ease-smooth-out",
    "hover:before:translate-x-0 hover:before:translate-y-0",
    // Hover lift stays; the press swaps the old scale-down for the lib's shared
    // offset-key language — a 1px press-DOWN (round/flat tier) on a springy
    // overshoot bounce, so the pill presses the same way every other control does.
    // (This is a round pill, so no hex notch / travel block / keyline ring.)
    "hover:scale-[1.02] active:translate-y-px",
    "motion-reduce:transition-none motion-reduce:before:transition-none motion-reduce:hover:scale-100 motion-reduce:active:translate-y-0",
    "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='w-'])]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "border-primary bg-primary text-primary-foreground before:bg-accent hover:border-accent hover:text-accent-foreground",
        secondary:
          "border-border bg-background text-primary before:bg-primary hover:border-primary hover:text-primary-foreground",
        ghost:
          "border-border/60 bg-transparent text-muted-foreground before:rounded-none before:bg-muted hover:border-border hover:text-foreground",
      },
      size: {
        sm: "gap-1.5 px-3 py-1.5 text-xs",
        md: "gap-2 px-4 py-2 text-sm",
        lg: "gap-2 px-5 py-2.5 text-sm",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface FlowButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof flowButtonVariants> {
  /** Optional leading icon, rendered before the label. */
  icon?: React.ReactNode;
}

export const FlowButton = React.forwardRef<HTMLButtonElement, FlowButtonProps>(
  ({ icon, children, variant, size, className, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(flowButtonVariants({ variant, size }), className)}
      {...props}
    >
      {icon ? <span className="relative z-10 flex items-center">{icon}</span> : null}
      {children != null ? <span className="relative z-10">{children}</span> : null}
    </button>
  ),
);
FlowButton.displayName = "FlowButton";
